import { useState, useEffect, useCallback, useRef } from 'react';
import { blogApi } from '../../../../utils/api';

/**
 * Hook for managing blog post CRUD operations in the Blog Editor
 */
const useBlogPosts = () => {
  const [posts, setPosts] = useState([]);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Track original post data for change detection
  const originalPostRef = useRef(null);

  // Fetch all posts (including drafts for admin)
  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const postList = await blogApi.listAll(); // All posts including drafts
      setPosts(postList);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
      setError('Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load posts on mount
  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Fetch full post details when selection changes
  useEffect(() => {
    const loadPostDetails = async () => {
      if (!selectedPostId) {
        setSelectedPost(null);
        originalPostRef.current = null;
        setHasChanges(false);
        return;
      }

      try {
        const post = await blogApi.get(selectedPostId);
        setSelectedPost(post);
        originalPostRef.current = JSON.stringify(post);
        setHasChanges(false);
      } catch (err) {
        console.error('Failed to load post details:', err);
        setError('Failed to load post details');
      }
    };

    loadPostDetails();
  }, [selectedPostId]);

  // Update selected post field
  const updatePostField = useCallback((field, value) => {
    setSelectedPost(prev => {
      if (!prev) return prev;
      const updated = { ...prev, [field]: value };
      // Check if there are changes
      const hasChanged = JSON.stringify(updated) !== originalPostRef.current;
      setHasChanges(hasChanged);
      return updated;
    });
  }, []);

  // Create new post
  const createPost = useCallback(async () => {
    const newPost = {
      title: 'New Post',
      slug: `new-post-${Date.now()}`,
      content: '# New Post\n\nStart writing your content here...',
      excerpt: '',
      featured_image: null,
      status: 'DRAFT'
    };

    try {
      setSaving(true);
      const created = await blogApi.create(newPost);
      await fetchPosts();
      setSelectedPostId(created.post_id);
      return created;
    } catch (err) {
      console.error('Failed to create post:', err);
      setError('Failed to create post');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [fetchPosts]);

  // Save current post
  const savePost = useCallback(async () => {
    if (!selectedPost || !hasChanges) return;

    try {
      setSaving(true);
      setError(null);
      
      const updates = {
        title: selectedPost.title,
        slug: selectedPost.slug,
        content: selectedPost.content,
        excerpt: selectedPost.excerpt,
        featured_image: selectedPost.featured_image,
        status: selectedPost.status
      };

      await blogApi.update(selectedPost.post_id, updates);
      originalPostRef.current = JSON.stringify(selectedPost);
      setHasChanges(false);
      await fetchPosts();
    } catch (err) {
      console.error('Failed to save post:', err);
      setError(err.message || 'Failed to save post');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [selectedPost, hasChanges, fetchPosts]);

  // Delete post
  const deletePost = useCallback(async (postId) => {
    const targetId = postId || selectedPostId;
    if (!targetId) return;

    try {
      setSaving(true);
      await blogApi.delete(targetId);
      
      if (selectedPostId === targetId) {
        setSelectedPostId(null);
        setSelectedPost(null);
      }
      
      await fetchPosts();
    } catch (err) {
      console.error('Failed to delete post:', err);
      setError('Failed to delete post');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [selectedPostId, fetchPosts]);

  // Publish/unpublish post
  const togglePublish = useCallback(async () => {
    if (!selectedPost) return;

    try {
      setSaving(true);
      if (selectedPost.status === 'PUBLISHED') {
        await blogApi.unpublish(selectedPost.post_id);
      } else {
        await blogApi.publish(selectedPost.post_id);
      }
      
      // Refresh post data
      const updated = await blogApi.get(selectedPost.post_id);
      setSelectedPost(updated);
      originalPostRef.current = JSON.stringify(updated);
      setHasChanges(false);
      await fetchPosts();
    } catch (err) {
      console.error('Failed to toggle publish:', err);
      setError('Failed to update publish status');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [selectedPost, fetchPosts]);

  // Revert changes
  const revertChanges = useCallback(() => {
    if (originalPostRef.current) {
      setSelectedPost(JSON.parse(originalPostRef.current));
      setHasChanges(false);
    }
  }, []);

  return {
    // State
    posts,
    selectedPostId,
    selectedPost,
    loading,
    saving,
    error,
    hasChanges,
    
    // Actions
    setSelectedPostId,
    updatePostField,
    createPost,
    savePost,
    deletePost,
    togglePublish,
    revertChanges,
    fetchPosts
  };
};

export default useBlogPosts;









